import { useCallback, useEffect, useRef, useState } from "react";
import {
  changeCheckBeatMs,
  notifyShowMoved,
  subscribeNudgeStore,
} from "common-fe/changeNudge";
import { useScoreApi, unwrap } from "../api/scoreApi";
import {
  BACKSTOP_REFRESH_MS,
  DEFAULT_CHANGE_POLL_MS,
  MIN_CHANGE_POLL_MS,
} from "../constants/polling";

// #3557 -- the announcer reloads only when something changed (docs/adr/0003).
//
// ONE cheap ask per beat, GET /show/:id/changes (score-api #3558), replaces the
// old 5 s blind re-read of classes / rides / breaks / ring-state / results.
// The answer is a set of counters; this hook compares it to the last answer
// and hands the screen four "ticks" (numbers that go up). A data hook that
// takes a tick as its refetch nonce re-reads exactly when its tick moves:
//
//   ringTick       this ring's counter moved, or showVersion moved.
//                  Rides, breaks, ring-state, results summary.
//   classTick      ringTick, or the SET of ring names changed. A class counter
//                  only ever moves together with its ring's counter (the
//                  server bumps both on one write), so watching the ring
//                  counter covers every class in the ring; the set of ring
//                  names covers a class added to / moved between rings.
//   showTick       showVersion moved (judge roster, show row).
//   wholeShowTick  the show-wide `total` moved, or showVersion. The all-rings
//                  list reads every ring, so any write anywhere is its cue.
//
// All four also bump on a BACKSTOP_REFRESH_MS timer, so a missed signal can
// leave the screen wrong for at most a minute.
//
// First answer after mount (or a show change) only ADOPTS the baseline: the
// screen already loaded its own data on mount, re-reading it would double the
// cold start.
//
// A failed check changes nothing: no tick, no `lastCheckedAt`. The screen goes
// stale on its own clock (isStale) instead of being told something moved.
//
// Owed once more (the scribe #3252 pattern). The rides / breaks reads are GSI
// queries (eventually consistent) and the counters are bumped by a separate
// async stream, so the read that follows a counter move can return rows from
// BEFORE the write that moved it. A move is therefore also "owed" one more
// tick on the NEXT beat, then settles: one extra read per move, no loop.
//
// Change nudges (card #3562, docs/adr/0003). The staff WebSocket (common-fe
// changeNudge, mounted once in RingDataContext) writes into a store this hook
// reads: a NUDGE runs a check at once, and while the socket is HEALTHY the beat
// slows to 30 s (the nudge is the fast path, the check only backstops it). The
// moment the socket drops the beat is the normal pollMs again and a check runs
// right then. A check that sees the show move also tells the store, so a socket
// released by Instant updates OFF can ask again whether it is ON now.
//
// Not covered here, by design: useClassResults' live-class results poll and
// the presence poll keep their own timers.
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function normalizePollMs(pollMs) {
  const n = Number(pollMs);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_CHANGE_POLL_MS;
  return Math.max(MIN_CHANGE_POLL_MS, Math.floor(n));
}

const ringNamesKey = (rings) => Object.keys(rings ?? {}).sort().join("\u0000");

export function useShowChangeCheck(showId, ring, { enabled = true } = {}) {
  const api = useScoreApi();
  const [ticks, setTicks] = useState({ ring: 0, class: 0, show: 0, whole: 0 });
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const ringRef = useRef(ring);
  ringRef.current = ring;
  const baselineRef = useRef(null);
  const owedRef = useRef({ ring: false, class: false, show: false, whole: false });
  const inflightRef = useRef(null);
  const timerRef = useRef(null);
  const pollMsRef = useRef(DEFAULT_CHANGE_POLL_MS);
  const checkRef = useRef(null);
  // Bumped on every show change / unmount: an answer to a check started under an
  // older generation belongs to the previous show and must not set a baseline.
  const genRef = useRef(0);

  const bump = useCallback((which) => {
    setTicks((prev) => {
      const next = { ...prev };
      for (const k of which) next[k] += 1;
      return next;
    });
  }, []);

  const check = useCallback(async () => {
    if (!showId) return;
    if (inflightRef.current) return inflightRef.current;
    const gen = genRef.current;
    const run = (async () => {
      let answer;
      try {
        answer = unwrap(await api.shows.getShowChanges(showId));
      } catch {
        return; // a failed check changes nothing
      }
      if (gen !== genRef.current) return; // the show changed while this was in flight
      pollMsRef.current = normalizePollMs(answer?.pollMs);
      const next = {
        total: num(answer?.total),
        showVersion: num(answer?.showVersion),
        rings: answer?.rings ?? {},
      };
      setLastCheckedAt(new Date());
      const prev = baselineRef.current;
      baselineRef.current = next;
      if (!prev) return; // first answer: adopt, read nothing

      const r = ringRef.current;
      const showMoved = next.showVersion !== prev.showVersion;
      const ringMoved = showMoved || (r != null && num(next.rings[r]) !== num(prev.rings[r]));
      const namesMoved = ringNamesKey(next.rings) !== ringNamesKey(prev.rings);
      const moved = {
        ring: ringMoved,
        class: ringMoved || namesMoved,
        show: showMoved,
        whole: showMoved || next.total !== prev.total,
      };
      if (showMoved || next.total !== prev.total) notifyShowMoved(showId);
      const owed = owedRef.current;
      const fire = Object.keys(moved).filter((k) => moved[k] || owed[k]);
      owedRef.current = moved; // a fresh move is owed one more read; an owed one settles
      if (fire.length) bump(fire);
    })();
    inflightRef.current = run;
    try {
      await run;
    } finally {
      inflightRef.current = null;
    }
  }, [api, showId, bump]);
  checkRef.current = check;

  useEffect(() => {
    genRef.current += 1;
    inflightRef.current = null; // the old show's check must not block the new show's
    baselineRef.current = null;
    owedRef.current = { ring: false, class: false, show: false, whole: false };
    if (!enabled || !showId) return undefined;

    let stopped = false;
    let beatMs = null;
    const hidden = () => document.visibilityState === "hidden";
    const clear = () => {
      if (timerRef.current != null) clearInterval(timerRef.current);
      timerRef.current = null;
      beatMs = null;
    };
    // setInterval, not a chained setTimeout: the beat is the same kind of timer
    // the old 5 s entity poll was, and an in-flight guard stops overlap. The
    // beat is rebuilt when the server recommends a different pollMs.
    const schedule = () => {
      clear();
      if (stopped || hidden()) return;
      beatMs = changeCheckBeatMs(showId, pollMsRef.current);
      timerRef.current = setInterval(async () => {
        await checkRef.current();
        if (
          !stopped &&
          timerRef.current != null &&
          beatMs !== changeCheckBeatMs(showId, pollMsRef.current)
        ) {
          schedule();
        }
      }, beatMs);
    };
    const onVisibility = async () => {
      if (hidden()) {
        clear();
        return;
      }
      clear();
      await checkRef.current();
      schedule();
    };

    // One immediate check that only adopts the baseline (skipped while hidden;
    // the visibility handler checks the moment the tab returns).
    if (!hidden()) {
      checkRef.current().then(schedule);
    }
    const backstop = setInterval(() => {
      if (hidden()) return;
      bump(["ring", "class", "show", "whole"]);
    }, BACKSTOP_REFRESH_MS);
    document.addEventListener("visibilitychange", onVisibility);
    // A nudge that lands while a check is already running must not be absorbed by
    // it: that check may have read the server BEFORE the write the nudge announces.
    // Let it finish, then ask once more (concurrent nudges coalesce into that one).
    const checkAfterNudge = async () => {
      const pending = inflightRef.current;
      if (pending) await pending;
      if (!stopped) await checkRef.current();
    };
    // Card #3562: the staff socket's nudges and health changes.
    const unsubscribeNudges = subscribeNudgeStore(showId, async (event) => {
      if (stopped || hidden()) return;
      if (event.kind === "nudge") {
        await checkAfterNudge();
      } else if (event.healthy) {
        schedule(); // healthy: slow the beat to 30 s
      } else {
        // down: the normal rate again, and catch up on what the socket may have missed
        schedule();
        await checkAfterNudge();
      }
    });
    return () => {
      genRef.current += 1;
      stopped = true;
      unsubscribeNudges();
      clear();
      clearInterval(backstop);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, showId, bump]);

  return {
    ringTick: ticks.ring,
    classTick: ticks.class,
    showTick: ticks.show,
    wholeShowTick: ticks.whole,
    lastCheckedAt,
    // Check right now (the nudge path runs the same function).
    checkNow: check,
  };
}

export default useShowChangeCheck;
